import React, { useEffect, useState, useContext } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';

export default function AdminVotesPage() {
  const { user } = useContext(AuthContext);
  const [votes, setVotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVotes();
  }, []);

  const fetchVotes = async () => {
    try {
      const response = await axios.get('/votes');
      setVotes(response.data);
    } catch (error) {
      console.error('Failed to fetch votes', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (voteId, choice) => {
    try {
      await axios.post(`/votes/${voteId}/cast`, { vote_choice: choice });
      toast.success('Voto registrato!');
      fetchVotes();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel voto'));
    }
  };

  const getVoteProgress = (vote) => {
    const total = vote.votes_yes + vote.votes_no;
    if (total === 0) return 0;
    return (vote.votes_yes / total) * 100;
  };

  const isVoteEnded = (vote) => {
    return new Date() > new Date(vote.end_date);
  };

  const hasUserVoted = (vote) => {
    return vote.voted_members.includes(user?.id);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-votes-page">
      <Navbar />

      <div className="container mx-auto px-6 pt-24 pb-12">
        <h1 className="text-4xl font-bold mb-12" data-testid="page-title">Votazioni</h1>

        {loading ? (
          <div className="text-center py-12">Caricamento...</div>
        ) : votes.length === 0 ? (
          <div className="text-center py-12 text-gray-600" data-testid="no-votes-message">
            Nessuna votazione attiva.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {votes.map((vote) => {
              const ended = isVoteEnded(vote);
              const voted = hasUserVoted(vote);
              const progress = getVoteProgress(vote);
              
              return (
                <Card key={vote.id} className="border-2 border-gray-200" data-testid="vote-card">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>{vote.candidate_name}</span>
                      {ended && (
                        <span className="text-xs font-normal bg-gray-200 text-gray-800 px-2 py-1 rounded">
                          Terminata
                        </span>
                      )}
                    </CardTitle>
                    <CardDescription>
                      Termina: {new Date(vote.end_date).toLocaleDateString('it-IT', { 
                        weekday: 'short', 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Vote Stats */}
                    <div className="space-y-4 mb-6">
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span>Sì: {vote.votes_yes}</span>
                          <span>No: {vote.votes_no}</span>
                        </div>
                        <Progress value={progress} className="h-2" />
                      </div>
                      <p className="text-sm text-gray-600">
                        Votanti: {vote.voted_members.length}
                      </p>
                    </div>

                    {/* Vote Actions */}
                    {!ended && !voted ? (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleVote(vote.id, 'yes')}
                          className="flex-1 bg-green-600 text-white hover:bg-green-700"
                          data-testid="vote-yes-button"
                        >
                          Vota Sì
                        </Button>
                        <Button
                          onClick={() => handleVote(vote.id, 'no')}
                          variant="outline"
                          className="flex-1 border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                          data-testid="vote-no-button"
                        >
                          Vota No
                        </Button>
                      </div>
                    ) : voted && !ended ? (
                      <div className="text-center py-2 bg-gray-100 rounded" data-testid="already-voted-message">
                        <p className="text-sm text-gray-700">✓ Hai già votato</p>
                      </div>
                    ) : ended && vote.result ? (
                      <div className={`text-center py-2 rounded ${
                        vote.result === 'accepted' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`} data-testid="vote-result">
                        <p className="font-semibold">
                          {vote.result === 'accepted' ? '✓ Candidatura Accettata' : '✗ Candidatura Rifiutata'}
                        </p>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
